import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { BrandingProvider } from './contexts/BrandingContext';
import { getIsStandalone } from './hooks/useIsStandalone';

// V2 CSS Only
import './styles/v2/index.css';

// =========================================================================
// Mark the installed PWA with a `.pwa-mode` class on <html> so CSS can gate
// PWA-only rules (safe-area inset, help-button hide) off a reliable class
// instead of `@media (display-mode: standalone)`, which does not match on iOS
// home-screen web apps. getIsStandalone() also checks navigator.standalone
// (iOS's authoritative signal). Run synchronously before render so the class
// is present on first paint.
// =========================================================================
function applyStandaloneClass() {
  document.documentElement.classList.toggle('pwa-mode', getIsStandalone());
}
applyStandaloneClass();
if (typeof window.matchMedia === 'function') {
  window.matchMedia('(display-mode: standalone)').addEventListener('change', applyStandaloneClass);
}

// =========================================================================
// Service Worker registration with auto-update.
//
// Old behavior: sw.js was byte-identical between builds, so browsers
// never detected updates and PWA users had to uninstall + reinstall.
// New behavior: build pipeline injects a unique BUILD_VERSION into sw.js,
// browser sees the bytes differ, the new SW installs and (because of
// skipWaiting + clients.claim) takes over immediately. The
// controllerchange handler below then reloads the page so the user
// runs the new app code without manual action.
// =========================================================================
if ('serviceWorker' in navigator) {
  // Snapshot whether this page already has a controlling SW. Used to
  // distinguish "first install" (do not reload) from "new SW took over"
  // (do reload).
  const hadControllerOnLoad = !!navigator.serviceWorker.controller;

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        // Tell the browser not to use HTTP cache when fetching sw.js itself.
        // Without this, an aggressive cache could mask a new SW indefinitely.
        updateViaCache: 'none'
      });
      console.log('[App] Service Worker registered:', registration.scope);

      // Periodic update check while the app is open.
      setInterval(() => {
        registration.update().catch(() => {});
      }, 30 * 60 * 1000);

      // Also check when the tab becomes visible again (common on phones
      // where the PWA sits in the background and the user comes back to it).
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => {});
        }
      });
    } catch (error) {
      console.error('[App] Service Worker registration failed:', error);
    }
  });

  // When a new SW takes control, reload so the user runs the new code.
  // Guard against (a) reloading on first install (no prior controller) and
  // (b) reload loops.
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerOnLoad) return;
    if (reloading) return;
    reloading = true;
    console.log('[App] New service worker took over, reloading');
    window.location.reload();
  });
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrandingProvider>
      <App />
    </BrandingProvider>
  </React.StrictMode>
);
